import { useEffect, useState, useMemo } from "react";
import * as ReactDOM from "react-dom";
import React from "react";

interface Reactify {
  module: (module: any) => any;
}

declare global {
  var ymaps3: any;
}

const MAP_API_URL =
  "https://api-maps.yandex.ru/v3/?apikey=7e6739a7-a7a8-4ee9-968d-de1b43b92452&lang=ru_RU";

const YMAPS_CHECK_INTERVAL = 100;

let reactifyCache: Reactify | null = null;
let scriptLoading = false;
let scriptLoaded = false;
let loadPromise: Promise<void> | null = null;

function isYmapsReady(): boolean {
  return typeof ymaps3 !== "undefined" && Boolean(ymaps3.import);
}

function waitForYmaps(): Promise<void> {
  return new Promise((resolve) => {
    const checkYmaps = setInterval(() => {
      if (isYmapsReady()) {
        clearInterval(checkYmaps);
        resolve();
      }
    }, YMAPS_CHECK_INTERVAL);
  });
}

async function loadYandexMapsScript(): Promise<void> {
  if (scriptLoaded && isYmapsReady()) {
    return;
  }

  if (loadPromise) {
    return loadPromise;
  }

  const existingScript = document.querySelector(`script[src="${MAP_API_URL}"]`);
  if (existingScript || scriptLoaded) {
    if (isYmapsReady()) {
      return;
    }
    loadPromise = waitForYmaps();
    return loadPromise;
  }

  scriptLoading = true;

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = MAP_API_URL;
    script.async = true;
    script.onload = () => {
      scriptLoaded = true;
      scriptLoading = false;
      if (isYmapsReady()) {
        if (!reactifyCache) {
          void loadReactify();
        }
        resolve();
      } else {
        reject(new Error("ymaps3 not available after script load"));
      }
    };
    script.onerror = () => {
      scriptLoading = false;
      loadPromise = null;
      reject(new Error("Failed to load Yandex Maps script"));
    };
    document.body.appendChild(script);
  });

  return loadPromise;
}

async function loadReactify(): Promise<Reactify> {
  if (reactifyCache) {
    return reactifyCache;
  }

  if (!isYmapsReady()) {
    await loadYandexMapsScript();
  }

  if (!isYmapsReady()) {
    throw new Error("ymaps3 is not available");
  }

  try {
    const ymaps3Reactify = await ymaps3.import("@yandex/ymaps3-reactify");
    const reactify = ymaps3Reactify.reactify.bindTo(React, ReactDOM);
    reactifyCache = reactify as Reactify;
    return reactifyCache;
  } catch (error) {
    throw error;
  }
}

export function preloadYandexMaps(): void {
  if (scriptLoaded || scriptLoading || loadPromise) {
    if (scriptLoaded && isYmapsReady() && !reactifyCache) {
      void loadReactify();
    }
    return;
  }

  void loadYandexMapsScript().then(() => {
    if (isYmapsReady() && !reactifyCache) {
      void loadReactify();
    }
  });
}

export function useYandexMaps() {
  // КРИТИЧНО: Если reactifyCache уже загружен, используем его напрямую без useState
  // Это предотвращает задержку при переходах между страницами
  // В telegram_mini_app используется такой же подход - reactify кешируется в модульной переменной
  const [reactify, setReactify] = useState<Reactify | null>(reactifyCache);

  useEffect(() => {
    // Если reactifyCache уже есть, синхронизируем состояние (но это не блокирует рендер)
    if (reactifyCache && reactify !== reactifyCache) {
      setReactify(reactifyCache);
      return;
    }

    // Если reactifyCache еще нет, загружаем асинхронно
    if (!reactifyCache) {
      void loadReactify()
        .then((loadedReactify) => {
          setReactify(loadedReactify);
        })
        .catch(() => {
          // Error handling is done by the service
        });
    }
  }, [reactify]);

  // КРИТИЧНО: Используем reactifyCache напрямую для мгновенного возврата Common
  // Это предотвращает задержку при переходах, когда карта уже загружена
  // Если reactifyCache уже есть, Common будет вычислен синхронно без задержки
  // Это аналогично подходу в telegram_mini_app, где reactify кешируется в модульной переменной
  const Common = useMemo(() => {
    // Приоритет отдаем reactifyCache (модульная переменная), затем reactify (state)
    const currentReactify = reactifyCache || reactify;
    return currentReactify && isYmapsReady() ? currentReactify.module(ymaps3) : null;
  }, [reactify]);

  return { Common };
}
